import {
  getFileType,
  capitalizeFirstLetter,
  getFileIconPath,
  getDisplayFileName,
  getDocClassCodeOnly,
  isWorkingDocumentCheckedOut,
  isDocumentReleased,
} from "../../../../functions/helperMethod";
import {
  DOCUMENT_MODE,
  FILE_STATE,
  STATE,
  IFS_ACCESS_LEVEL,
} from "../../../../redux/constants/metaData";
import { getDocumentClassFormatLabel } from "../../../../functions/getDocumentClassFormatLabel";
import { resolveLastAction } from "../../../../functions/resolveDocumentListLastAction";
import { formatDate } from "../../../../functions/dateFormater";

function getCheckoutAccessLevel(accessLevel) {
  switch ((accessLevel || "").toUpperCase()) {
    case IFS_ACCESS_LEVEL.admin:
    case IFS_ACCESS_LEVEL.edit:
      return false;
    case IFS_ACCESS_LEVEL.view:
    case IFS_ACCESS_LEVEL.noAccess:
    case IFS_ACCESS_LEVEL.noData:
    default:
      return true;
  }
}

function getPreviewAccessLevel(accessLevel) {
  switch ((accessLevel || "").toUpperCase()) {
    case IFS_ACCESS_LEVEL.admin:
    case IFS_ACCESS_LEVEL.edit:
    case IFS_ACCESS_LEVEL.view:
      return false;
    case IFS_ACCESS_LEVEL.noAccess:
    case IFS_ACCESS_LEVEL.noData:
    default:
      return true;
  }
}

function getIsAccessible(accessLevel) {
  switch ((accessLevel || "").toUpperCase()) {
    case IFS_ACCESS_LEVEL.admin:
    case IFS_ACCESS_LEVEL.edit:
    case IFS_ACCESS_LEVEL.view:
      return true;
    case IFS_ACCESS_LEVEL.noAccess:
    case IFS_ACCESS_LEVEL.noData:
    default:
      return false;
  }
}

function getCheckedOutStatus(fileState) {
  return fileState === FILE_STATE.checkedOut;
}

function getApprovedStatus(docState) {
  return docState === STATE.approved;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text !== "") return text;
  }
  return "";
}


function stripCodePrefix(label, code) {
  const text = (label ?? "").trim();
  const prefix = `${(code ?? "").trim()} - `;
  return code && text.startsWith(prefix) ? text.slice(prefix.length).trim() : text;
}

/**
 * Maps IFS document list + working document list into table row items for the All Documents grid.
 * @param {Array} documents - Raw document list from search/filter
 * @param {Array} [workingDocumentList] - Working documents (OneDrive) for matching
 * @param {Object} [attachedDocInfo] - Recently checked-in doc for "new" badge
 * @returns {Array} Row items for DetailsList
 */
export function getDocumentTableItems(
  documents = [],
  workingDocumentList = [],
  attachedDocInfo = null,
  docClassFormatList = []
) {
  const recentDoc = attachedDocInfo;
  if (!documents || !Array.isArray(documents)) return [];

  const mappedItems = documents.map((item) => {
    const docClassCode = firstNonEmpty(item.docClass, item.DocClass, item.class, item.Class);
    const formatSizeCode = firstNonEmpty(
      item.FormatSize,
      item.formatSize,
      item.format,
      item.Format
    );
    const apiFormatSizeName = firstNonEmpty(
      item.FormatSizeName,
      item.formatSizeName,
      item.formatName,
      item.FormatName,
      item.DocFormatDescription,
      item.docFormatDescription
    );

    const classNode =
      docClassCode && Array.isArray(docClassFormatList)
        ? docClassFormatList.find(
            (x) =>
              firstNonEmpty(x?.code, x?.docClass, x?.DocClass, x?.class, x?.Class) ===
              docClassCode
          )
        : null;

    let classDisplayName = firstNonEmpty(item.DocClassName, item.docClassName);
    if (!classDisplayName && classNode) {
      classDisplayName =
        stripCodePrefix(firstNonEmpty(classNode.name, classNode.Name), docClassCode) ||
        firstNonEmpty(classNode.docName, classNode.DocName);
    }

   
    let resolvedFormatName = apiFormatSizeName;
    if (!resolvedFormatName && formatSizeCode && classNode) {
      const formats = classNode.children ?? classNode.formats ?? classNode.Formats ?? [];
      if (Array.isArray(formats)) {
        const formatNode = formats.find(
          (f) =>
            firstNonEmpty(f?.code, f?.formatSize, f?.FormatSize, f?.format, f?.Format) ===
            formatSizeCode
        );
        if (formatNode) {
          resolvedFormatName =
            firstNonEmpty(
              formatNode.formatSizeName,
              formatNode.FormatSizeName,
              formatNode.formatName,
              formatNode.FormatName
            ) ||
            stripCodePrefix(firstNonEmpty(formatNode.name, formatNode.Name), formatSizeCode);
        }
      }
    }

    const isRecentCheckIn =
      recentDoc &&
      getDocClassCodeOnly(docClassCode) === getDocClassCodeOnly(recentDoc.docClass) &&
      item.docNo === recentDoc.docNo &&
      item.docRev === recentDoc.docRev &&
      item.docSheet === recentDoc.docSheet;


    const isDownloadDisable = getCheckoutAccessLevel(item.AccessLevel);
    const iconPath = getFileIconPath(item.FileType, item);
    const lastActionInfo = resolveLastAction(item);

    return {
      key: item.Id,
      iconName: iconPath,
      documentNo: item.docNo + " - " + item.docRev,
      title: getDisplayFileName(item.Title ?? ""),
      documentClassFormat: getDocumentClassFormatLabel(
        docClassCode,
        resolvedFormatName
      ),
      documentBasic: `${docClassCode} - ${item.docNo} - ${item.docSheet} - ${item.docRev}`,
      titleInCapitalized: capitalizeFirstLetter(getDisplayFileName(item.Title ?? "")),
      checkedInDate: formatDate(item.CheckedInDate),
      checkedOutDate: formatDate(item.CheckedOutDate),
      fileSize: 0,
      sizeInbytes: item.FileSize,
      owner: item.DocRespName,
      class: docClassCode,
      className: classDisplayName,
      format: formatSizeCode,
      formatName: resolvedFormatName,
      sheetNo: item.docSheet,
      state: item.State,
      fileState: item.FileState,
      status: item.Status,
      docNo: item.docNo,
      docRev: item.docRev,
      docType: item.DocType,
      fileNo: item.FileNo,
      fileExtension: item.FileExtension,
      fileType: getFileType(item.FileExtension, item.FileType),
      lastActionDate: lastActionInfo.display,
      lastActionTimestamp: lastActionInfo.timestamp,
      lastAction: item.LastAction,
      viewStatus: item.ViewStatus,
      viewFileType: item.ViewFileType,
      ViewIconName:
        item.ViewFileExtension != null
          ? getFileIconPath(item.ViewFileType, {
              FileType: item.ViewFileType,
              FileExtension: item.ViewFileExtension,
              FileState: item.FileState,
              Status: item.Status,
              State: item.State,
            })
          : null,
      viewFileExtension: item.ViewFileExtension,
      documentMode: DOCUMENT_MODE.ifs,
      isCheckoutDisable:
        getCheckoutAccessLevel(item.AccessLevel) ||
        isDocumentReleased(item) ||
        getApprovedStatus(item.Status) ||
        getApprovedStatus(item.State),
      isCheckedOut:
        getCheckedOutStatus(item.FileState) || isWorkingDocumentCheckedOut(item),
      isReleased: isDocumentReleased(item),
      isPreviewDisable: getPreviewAccessLevel(item.AccessLevel),
      isDownloadDisable,
      isAccessible: getIsAccessible(item.AccessLevel),
      isRecentCheckIn,
    };
  });

  return mappedItems;
}
